import * as Sentry from '@sentry/react'

import { getSessionManager } from './sessionManager'
import {
  BaseEvent,
  GuestAnalyticsConfig,
  ConversionData,
  GuestAnalyticsEvent
} from './types'
import {
  TrackingEvents,
  CONSENT_EXEMPT_EVENTS,
  FULFILLMENT_EVENTS
} from 'src/shared/components/common/tracking/TrackingEvents'

type FlushTrigger = 'flush' | 'unload'

const MAX_FLUSH_FAILURES = 3

/**
 * Guest Analytics Client
 *
 * A first-party analytics system for tracking user behavior and conversion events
 * on Toast restaurant websites. Unlike third-party analytics tools (e.g., Amplitude),
 * Guest Analytics data is owned and controlled by Toast.
 *
 * Key capabilities:
 * - Session tracking: Monitors user sessions and time spent on site
 * - Traffic attribution: Classifies traffic into channels (Direct, Organic Search, Paid Social, etc.)
 *   and captures traffic sources (e.g., google, facebook) and referrer information
 * - Conversion tracking: Tracks orders, reservations, gift card purchases, and signups
 * - Batched event delivery: Queues events and sends in batches to optimize performance
 * - Page exit tracking: Uses navigator.sendBeacon() to capture events when users leave
 * - Consent management: Handles user consent for session data tracking
 *
 * Events are sent to the guest-analytics-intake service and stored in Toast's
 * data catalog for restaurants to understand customer behavior and improve their operations.
 */
export class GuestAnalyticsClient {
  private config: Required<GuestAnalyticsConfig>
  private eventQueue: GuestAnalyticsEvent[] = []
  private pendingConsentQueue: GuestAnalyticsEvent[] = []
  private hasConsent: boolean | null = null
  private unloadHandler: (() => void) | null = null
  private visibilityHandler: (() => void) | null = null
  private flushFailures = 0

  constructor(config: GuestAnalyticsConfig) {
    this.config = {
      batchSize: config.batchSize || 5,
      ...config
    }

    if (typeof window !== 'undefined') {
      this.setupUnloadListeners()
    }
  }

  /**
   * Set up event listeners to flush analytics data when the user exits the page.
   * Uses multiple events to ensure maximum coverage across different browsers and scenarios:
   * - visibilitychange: When tab becomes hidden (mobile browsers, tab switching)
   * - pagehide: When page is about to be unloaded (iOS Safari)
   * - beforeunload: Traditional unload event (desktop browsers)
   */
  private setupUnloadListeners(): void {
    if (typeof window === 'undefined') {
      return
    }
    this.unloadHandler = () => {
      this.flush('unload')
    }

    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.flush('unload')
      }
    }

    // visibilitychange: Best for mobile browsers and tab switching
    document.addEventListener('visibilitychange', this.visibilityHandler)

    // pagehide: Important for iOS Safari and mobile browsers
    window.addEventListener('pagehide', this.unloadHandler)

    // beforeunload: Traditional desktop browser support
    window.addEventListener('beforeunload', this.unloadHandler)
  }

  destroy(): void {
    if (typeof window === 'undefined') {
      return
    }

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }

    if (this.unloadHandler) {
      window.removeEventListener('pagehide', this.unloadHandler)
      window.removeEventListener('beforeunload', this.unloadHandler)
      this.unloadHandler = null
    }
  }

  private createBaseEvent(eventType: string): BaseEvent {
    const sessionManager = getSessionManager()

    const event: BaseEvent = {
      siteGuid: this.config.siteGuid,
      timestamp: Date.now(),
      eventType: eventType,
      sessionGuid: sessionManager.getSessionGuid(),
      timeInSession: sessionManager.getTimeInSession()
    }

    // Get traffic attribution from session manager
    const trafficAttribution = sessionManager.getTrafficAttribution()
    if (trafficAttribution.trafficChannel) {
      event.trafficChannel = trafficAttribution.trafficChannel
      if (trafficAttribution.trafficSource)
        event.trafficSource = trafficAttribution.trafficSource
      if (trafficAttribution.referrer)
        event.referrer = trafficAttribution.referrer
    }

    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href)
        event.pageDomain = url.hostname
        event.pagePath = url.pathname
      } catch {}
    }

    return event
  }

  private stripForConsent(event: GuestAnalyticsEvent): GuestAnalyticsEvent {
    if (event.eventType === TrackingEvents.SESSION_START) {
      return {
        eventType: event.eventType,
        siteGuid: event.siteGuid,
        timestamp: event.timestamp
      }
    }

    const {
      sessionGuid,
      timeInSession,
      conversionId,
      referrer,
      trafficChannel,
      trafficSource,
      ...stripped
    } = event
    return stripped
  }

  setConsent(consent: boolean): void {
    this.hasConsent = consent
    getSessionManager().setConsent(consent)
    this.processPendingConsentQueue()
  }

  private processPendingConsentQueue(): void {
    if (this.pendingConsentQueue.length === 0) {
      return
    }

    if (this.hasConsent) {
      // Consent granted: send all events with full data
      this.eventQueue.push(...this.pendingConsentQueue)
    } else if (this.hasConsent === false) {
      // Consent denied: only process consent-exempt events with stripped data
      this.pendingConsentQueue.forEach((event) => {
        if (CONSENT_EXEMPT_EVENTS.has(event.eventType as TrackingEvents)) {
          this.eventQueue.push(this.stripForConsent(event))
        }
      })
    }

    this.pendingConsentQueue = []
    this.flush()
  }

  trackEvent(eventType: string, data?: ConversionData): void {
    const sessionManager = getSessionManager()
    const previousSessionGuid = sessionManager.getSessionGuid()
    sessionManager.updateActivity()

    if (this.flushFailures >= MAX_FLUSH_FAILURES) {
      return
    }

    const sessionReset = sessionManager.getSessionGuid() !== previousSessionGuid

    if (sessionReset && eventType !== TrackingEvents.SESSION_START) {
      this.trackEvent(TrackingEvents.SESSION_START)
    }

    // Before Ethyca loads: queue all events with full data
    if (this.hasConsent === null) {
      const baseEvent = this.createBaseEvent(eventType)
      this.pendingConsentQueue.push({ ...baseEvent, ...data })
      return
    }

    const fullEvent: GuestAnalyticsEvent = {
      ...this.createBaseEvent(eventType),
      ...data
    }

    if (this.hasConsent === false) {
      if (CONSENT_EXEMPT_EVENTS.has(eventType as TrackingEvents)) {
        this.eventQueue.push(this.stripForConsent(fullEvent))
      }
    } else {
      this.eventQueue.push(fullEvent)
    }

    if (
      FULFILLMENT_EVENTS.has(eventType as TrackingEvents) ||
      this.eventQueue.length >= this.config.batchSize
    ) {
      this.flush()
    }
  }

  flush(action: FlushTrigger = 'flush'): void {
    if (
      this.flushFailures >= MAX_FLUSH_FAILURES ||
      this.eventQueue.length === 0
    ) {
      return
    }

    const eventsToSend = [...this.eventQueue]
    this.eventQueue = []

    fetch(this.config.apiEndpoint, {
      method: 'POST',
      body: JSON.stringify(eventsToSend),
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'x-site-guid': this.config.siteGuid
      }
    })
      .then(() => {
        this.flushFailures = 0
      })
      .catch((error) => {
        // A content blocker cancels the request while online; a genuinely
        // offline guest (mobile handoff, tunnel) should not burn the retry
        // budget — re-queue and wait for connectivity to return.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          this.eventQueue = [...eventsToSend, ...this.eventQueue]
          return
        }

        this.flushFailures++

        if (this.flushFailures >= MAX_FLUSH_FAILURES) {
          return
        }

        Sentry.captureException(error, {
          tags: {
            module: 'guest-analytics',
            action: action
          },
          extra: {
            siteGuid: this.config.siteGuid,
            apiEndpoint: this.config.apiEndpoint,
            eventCount: eventsToSend.length,
            queuedEvents: this.eventQueue.length,
            flushFailures: this.flushFailures
          }
        })

        // Re-queue events for retry
        this.eventQueue = [...eventsToSend, ...this.eventQueue]
      })
  }
}
